import { Table, TableStatus, UserRole, AppState, TableAction, ActionContext } from '../types';

// Hilfsfunktionen für Tisch-Operationen
export const createInitialTable = (id: number, number: string): Table => ({
  id,
  number,
  status: TableStatus.AVAILABLE,
  actions: {
    kitchenStarted: false,
    pizzaConfirmed: false,
    kitchenReady: false,
    pizzaReady: false,
  },
  actionTimestamps: {}, // Leer initialisieren
  lastUpdated: new Date().toISOString(),
  updatedBy: UserRole.KITCHEN
});

// Erstelle initiale App State
export const createInitialAppState = (tableCount: number): AppState => {
  const tables = Array.from({ length: tableCount }, (_, i) => 
    createInitialTable(i + 1, `#${i + 1}`)
  );
  
  return {
    tables,
    totalTables: tableCount,
    lastSync: new Date().toISOString(),
    version: 1
  };
};

// Bestimme Tischstatus basierend auf Actions
export const calculateTableStatus = (table: Table): TableStatus => {
  const { actions } = table;
  
  // Sichere Prüfung auf cancelledBy (optionales Property)
  if (actions.cancelledBy !== undefined) {
    return TableStatus.CANCELLED;
  }
  
  if (actions.kitchenReady && actions.pizzaReady) {
    return TableStatus.READY;
  }
  
  // Spezialfall: Küche fertig, aber Pizza noch nicht bestätigt
  if (actions.kitchenReady && !actions.pizzaConfirmed) {
    return TableStatus.KITCHEN_READY_PENDING;
  }
  
  // Normale PARTIALLY_READY: Eine Partei fertig UND Pizza bestätigt
  if ((actions.kitchenReady || actions.pizzaReady) && actions.pizzaConfirmed) {
    return TableStatus.PARTIALLY_READY;
  }
  
  if (actions.kitchenStarted) {
    return actions.pizzaConfirmed ? TableStatus.CONFIRMED : TableStatus.COOKING;
  }
  
  return TableStatus.AVAILABLE;
};

// Prüfe ob eine Aktion für einen Tisch und Rolle erlaubt ist
export const isActionAllowed = (table: Table, action: TableAction, role: UserRole): boolean => {
  const status = calculateTableStatus(table);
  const { actions } = table;
  
  switch (action) {
    case TableAction.START_COOKING:
      return role === UserRole.KITCHEN && status === TableStatus.AVAILABLE;
      
    case TableAction.CONFIRM_PIZZA:
      return role === UserRole.PIZZA && (status === TableStatus.COOKING || status === TableStatus.KITCHEN_READY_PENDING) && actions.kitchenStarted;
      
    case TableAction.MARK_READY:
      if (role === UserRole.KITCHEN) {
        return (status === TableStatus.COOKING || status === TableStatus.CONFIRMED || status === TableStatus.PARTIALLY_READY) && !actions.kitchenReady;
      }
      if (role === UserRole.PIZZA) {
        return (status === TableStatus.CONFIRMED || status === TableStatus.PARTIALLY_READY) && !actions.pizzaReady;
      }
      return false;
      
    case TableAction.MARK_SERVED:
      return status === TableStatus.READY;
      
    case TableAction.CANCEL:
      return (status === TableStatus.COOKING || status === TableStatus.CONFIRMED || status === TableStatus.KITCHEN_READY_PENDING) && actions.cancelledBy === undefined;
      
    case TableAction.CONFIRM_CANCEL:
      return status === TableStatus.CANCELLED && actions.cancelledBy !== undefined && actions.cancelledBy !== role;
      
    default:
      return false;
  }
};

// Wende eine Aktion auf einen Tisch an
export const applyAction = (table: Table, context: ActionContext): Table => {
  if (!isActionAllowed(table, context.action, context.role)) {
    return table;
  }
  
  const updatedTable: Table = {
    ...table,
    lastUpdated: context.timestamp,
    updatedBy: context.role,
    actions: { ...table.actions },
    actionTimestamps: { ...table.actionTimestamps } // Kopiere bestehende Timestamps
  };
  
  switch (context.action) {
    case TableAction.START_COOKING:
      updatedTable.actions.kitchenStarted = true;
      updatedTable.actionTimestamps.kitchenStarted = context.timestamp;
      break;
      
    case TableAction.CONFIRM_PIZZA:
      updatedTable.actions.pizzaConfirmed = true;
      updatedTable.actionTimestamps.pizzaConfirmed = context.timestamp;
      break;
      
    case TableAction.MARK_READY:
      if (context.role === UserRole.KITCHEN) {
        updatedTable.actions.kitchenReady = true;
        updatedTable.actionTimestamps.kitchenReady = context.timestamp;
      } else {
        updatedTable.actions.pizzaReady = true;
        updatedTable.actionTimestamps.pizzaReady = context.timestamp;
      }
      break;
      
    case TableAction.MARK_SERVED:
      // Reset to initial state
      updatedTable.actions = {
        kitchenStarted: false,
        pizzaConfirmed: false,
        kitchenReady: false,
        pizzaReady: false,
      };
      updatedTable.actionTimestamps.served = context.timestamp;
      // Lösche alle anderen Timestamps da Tisch zurückgesetzt wird
      updatedTable.actionTimestamps = {
        served: context.timestamp
      };
      break;
      
    case TableAction.CANCEL:
      updatedTable.actions.cancelledBy = context.role;
      updatedTable.actionTimestamps.cancelled = context.timestamp;
      break;
      
    case TableAction.CONFIRM_CANCEL:
      // Reset to initial state
      updatedTable.actions = {
        kitchenStarted: false,
        pizzaConfirmed: false,
        kitchenReady: false,
        pizzaReady: false,
      };
      updatedTable.actionTimestamps.served = context.timestamp; // Behandle wie serviert
      // Lösche alle anderen Timestamps da Tisch zurückgesetzt wird
      updatedTable.actionTimestamps = {
        served: context.timestamp
      };
      break;
  }
  
  updatedTable.status = calculateTableStatus(updatedTable);
  return updatedTable;
};

// Bekomme verfügbare Aktionen für einen Tisch und Rolle
export const getAvailableActions = (table: Table, role: UserRole): TableAction[] => {
  return Object.values(TableAction).filter(action => 
    isActionAllowed(table, action, role)
  );
};

// Bekomme UI-Text für Aktionen
export const getActionText = (action: TableAction, role: UserRole): string => {
  switch (action) {
    case TableAction.START_COOKING:
      return 'Pizza zubereiten';
    case TableAction.CONFIRM_PIZZA:
      return 'Zubereitung gestartet';
    case TableAction.MARK_READY:
      return role === UserRole.KITCHEN ? 'Küche fertig' : 'Pizza fertig';
    case TableAction.MARK_SERVED:
      return 'Serviert';
    case TableAction.CANCEL:
      return 'Storno';
    case TableAction.CONFIRM_CANCEL:
      return 'Storno bestätigen';
    default:
      return action;
  }
};

// Bekomme CSS-Klasse für Tischstatus
export const getStatusColor = (status: TableStatus): string => {
  switch (status) {
    case TableStatus.AVAILABLE:
      return 'table-available';    // Weiß
    case TableStatus.COOKING:
      return 'table-cooking';      // Orange
    case TableStatus.CONFIRMED:
      return 'table-confirmed';    // Orange mit Indikator
    case TableStatus.READY:
      return 'table-ready';        // Grün
    case TableStatus.CANCELLED:
      return 'table-cancelled';    // Rot
    default:
      return 'table-available';
  }
};